import type { Material, MeshPhysicalMaterial, Object3D } from '@anov/3d-core'
import { AmbientLight, Box3, Color, Mesh, MeshBasicMaterial, PlaneGeometry, Vector3, gsap, use } from '@anov/3d-core'
import { SurfaceMaterial } from '../../'
import ObjectLayer from '../Base/ObjectLayer'
import { finirheightTransitionEffect, finirlowEffect, inject, injectToBaseWrite, injectToFinirMaterial, injectToFrameFade, injectToGround, injectToRealFade } from './inject'
import type { MaterialSwitchSystemInterface } from '.'

type MaterialSwitchType = 'Real' | 'Fresnel' | 'Frame' | 'Pure'

export enum SwitchType {
  Real = 'Real',
  Fresnel = 'Fresnel',
  Frame = 'Frame',
  Pure = 'Pure',
}

interface MaterialSwitchOption {
  duration?: number
  center?: Vector3
  isApi?: boolean
}

interface MaterialSwitchUniforms {
  progress: { value: number }
  isFresnelFadeIn: { value: boolean }
  isRealFadeIn: { value: boolean }
  isPureFadeIn: { value: boolean }
  isFrameFadeIn: { value: boolean }
  maxSize: { value: number }
  center: { value: Vector3 }
  time: { value: number }
  isApi: { value: boolean }
  height: { value: number }
}

type RuleItem = {
  type: string
  keys: string[] | string
  material: string
  limit?: SwitchType
}

const DEFAULT_CENTER = new Vector3()
const GROUND_Y_OFFSET = -0.1
const SIZE_SCALE_FACTOR = 1.2
const GROUND_MATCHING = ['Ground', 'Road', 'Yard']

/**
 * 材质切换系统
 */
export class MaterialSwitchSystem extends ObjectLayer implements MaterialSwitchSystemInterface {
  public type: MaterialSwitchType = SwitchType.Real
  public originalMaterial = new Map()
  public uniforms: MaterialSwitchUniforms

  private objects = new Map<string, Object3D>()
  private objectsMap: Record<MaterialSwitchType, Map<string, Object3D>> = {
    Real: new Map(),
    Frame: new Map(),
    Pure: new Map(),
    Fresnel: new Map(),
  }

  private frame: ReturnType<typeof use.useframe> | null = null
  private gsap: gsap.core.Tween | null = null
  private duration: number | undefined
  private center: Vector3
  private isGenPlane = false
  private rules: RuleItem[] = []

  constructor(options?: MaterialSwitchOption, sceneControl?: any) {
    super(sceneControl)
    const { duration, center = DEFAULT_CENTER, isApi = false } = options ?? {}

    this.duration = duration
    this.center = center

    // 暂时注释：材质切换逻辑（2026.03.09）
    // this.uniforms = this.initializeUniforms()
    // this.uniforms.isApi.value = isApi
    // this.initializeBuiltInShader()

    // 暂时注释：材质切换逻辑（2026.03.09）
    // use.useframe(this.sceneControl, () => {
    //   this.uniforms.time.value += 0.00018
    //   this.uniforms.height.value -= 1
    // })
  }

  private initializeUniforms(): MaterialSwitchUniforms {
    return {
      progress: { value: 1.0 },
      isFresnelFadeIn: { value: false },
      isRealFadeIn: { value: true },
      isPureFadeIn: { value: false },
      isFrameFadeIn: { value: false },
      maxSize: { value: 0 },
      center: { value: this.center },
      time: { value: 0 },
      height: { value: 0 },
      isApi: { value: false },
    }
  }

  private initializeBuiltInShader() {
    SurfaceMaterial.getMaterialAll()
  }

  private hideAllObjects(): void {
    Object.values(this.objectsMap).forEach((objectMap) => {
      objectMap.forEach((object) => {
        object.visible = false
      })
    })
  }

  private showObjectsByType(type: MaterialSwitchType): void {
    this.objectsMap[type].forEach((object) => {
      object.visible = true
    })
  }

  public switchType(type: MaterialSwitchType,
    option?:
    {
      mode: 'auto' | 'time'
      duration?: number
      center?: number[]
      genPlane?: boolean
      rules?: RuleItem[]
    }): void {
    if (this.type === type)
      return

    const light = use.useScene(this.sceneControl).scene.getObjectByName('FresnelLight')

    if (light)
      light.removeFromParent()

    if (type !== SwitchType.Real) {
      const light = new AmbientLight(0xFFFFFF, 1.5)
      light.name = 'FresnelLight'
      use.useScene(this.sceneControl).scene.add(light)

      if (type === SwitchType.Pure)
        light.intensity = 0.5
    }

    if (option?.rules?.length > 0) {
      this.rules = option.rules
    }
    else {
      this.rules = [{
        type: 'matchName',
        keys: [
          'Tall',
        ],
        material: 'MfresnelGlow',
      },
      {
        type: 'matchName',
        keys: [
          'Fence',
        ],
        material: 'Moriginal',
      },
      {
        type: 'matchName',
        keys: [
          'Low',
        ],
        material: 'MlowFinir',
      },
      {
        type: 'matchName',
        keys: [
          'Window',
        ],
        material: 'Fresnel_Transparent',
        limit: SwitchType.Fresnel,
      }]
    }

    this.handleApiOption(option)
    this.handleMatch(type)
    this.cleanupAnimations()
    this.prepareForSwitch(type)
    this.startSwitchAnimation(type)

    this.type = type
  }

  private handleApiOption(option: {
    mode: 'auto' | 'time'
    duration?: number
    center?: number[]
    genPlane?: boolean
  }): void {
    const { mode = 'auto', duration = 2, center = [0, 0, 0], genPlane = true } = option ?? {}
    if (mode === 'auto')
      this.duration = undefined
    else
      this.duration = duration

    this.uniforms.center.value = new Vector3().fromArray(center)
    this.isGenPlane = genPlane
  }

  private cleanupAnimations(): void {
    this.frame?.()
    this.gsap?.kill()
  }

  private prepareForSwitch(newType: MaterialSwitchType): void {
    this.hideAllObjects()
    this.showObjectsByType(newType)
    this.showObjectsByType(this.type)

    const groundObject = this.objectsMap[newType].get('ground')
    const lastGroundObject = this.objectsMap[this.type].get('ground')

    if (groundObject)
      groundObject.visible = this.isGenPlane
    if (lastGroundObject)
      lastGroundObject.visible = lastGroundObject.userData.isVisible

    this.uniforms.progress.value = 0.0
    this.uniforms[`is${this.type}FadeIn`].value = false
    this.uniforms[`is${newType}FadeIn`].value = true
  }

  private startSwitchAnimation(newType: MaterialSwitchType): void {
    const lastType = this.type

    const onComplete = () => {
      this.objectsMap[lastType].forEach((object) => {
        object.visible = false
      })
      this.dispatchEvent({ type: 'onStyleChanged', style: newType } as any)
    }

    requestAnimationFrame(() => {
      if (this.duration)
        this.animateWithGSAP(onComplete)
      else
        this.animateWithFrame(onComplete)
    })
  }

  private animateWithGSAP(onComplete: () => void): void {
    this.gsap = gsap.to(this.uniforms.progress, {
      value: 1.0,
      duration: this.duration,
      ease: 'linear',
      onComplete,
    })
  }

  private async beginBySpeed(distance: number, onFinish?: () => void): Promise<void> {
    if (distance === 0)
      distance = 0.01

    let currDistance = 0
    let speed = 0
    let acc = 0

    this.frame = use.useframe(this.sceneControl, (deltaTime) => {
      acc += distance * deltaTime
      speed += acc * deltaTime
      currDistance += speed * deltaTime

      this.uniforms.progress.value = Math.min(1, currDistance / distance)

      if (currDistance >= distance) {
        this.frame?.()
        onFinish?.()
      }
    })
  }

  private animateWithFrame(onComplete: () => void): void {
    this.beginBySpeed(this.getMaxSize(), onComplete)
  }

  public addObject(object: Object3D): void {
    return
    // 暂时注释：材质切换逻辑（2026.03.09）
    this.switchType(SwitchType.Real)
    const uuid = this.getObjectUuid(object)
    this.objects.set(uuid, object)

    this.handleNormalObject(object, uuid)
    this.updateVisibility()
  }

  private getObjectUuid(object: Object3D): string {
    // @ts-ignore
    return object.tag === 'entity' ? object.entityId : object.uuid
  }

  private handleGroundObject(type: MaterialSwitchType): void {
    if (this.objectsMap[type].has('ground')) {
      this.updateGroundMesh(type)
    }
    else {
      const groundMesh = this.createGroundMesh()
      injectToGround(groundMesh, type, this.uniforms)
      this.objectsMap[type].set('ground', groundMesh)
      this.add(groundMesh)
    }

    const groundObject = this.objectsMap[type].get('ground')

    if (groundObject)
      this.isGenPlane ? groundObject.userData.isVisible = true : groundObject.userData.isVisible = false
  }

  private handleGroundMatching(type: MaterialSwitchType) {
    if (type === SwitchType.Real)
      return

    let isMatch = false

    this.objectsMap[type].forEach((object) => {
      object.traverse((child) => {
        if (GROUND_MATCHING.some(matching => child.name.includes(matching))) {
          if (this.isGenPlane)
            child.visible = false
          else
            child.visible = true

          isMatch = true
        }
      })
    })

    if (isMatch)
      this.handleGroundObject(type)
  }

  private async getMaterialByName(name: string) {
    const material = await SurfaceMaterial.getMaterial(name)

    return material
  }

  private async replaceMaterial(object: Mesh, materialName: string, type: string, limit?: SwitchType) {
    if (object.userData.isMatched)
      return

    if (materialName === 'MfresnelGlow') {
      if (type === SwitchType.Fresnel)
        object.material = finirheightTransitionEffect(object.material as Material, this.uniforms)
    }
    else if (materialName === 'Moriginal' || !materialName) {
      object.material = object.userData.originalMaterial
    }
    else if (materialName === 'MlowFinir') {
      if (type === SwitchType.Fresnel)
        object.material = finirlowEffect(object.material as Material, this.uniforms)
    }
    else {
      if (limit && limit !== type)
        return

      const material = await this.getMaterialByName(materialName)
      object.material = inject(material, type as 'Fresnel' | 'Frame' | 'Pure', this.uniforms) as MeshPhysicalMaterial
    }

    object.userData.isMatched = true
  }

  private resetMaterial(object: Mesh, type: MaterialSwitchType) {
    this[`generate${type}Object`](object)
  }

  private handleNameMatching(rule: RuleItem, type: MaterialSwitchType) {
    const { keys, material = 'Moriginal' } = rule
    let keysArray = []

    if (typeof keys === 'string')
      keysArray = [keys]
    else
      keysArray = keys

    this.objectsMap[type].forEach((object) => {
      object.traverse((child) => {
        if (child.type === 'Mesh') {
          const mesh = child as Mesh

          if (keysArray.some(matching => mesh.name.includes(matching) || mesh?.parent?.name.includes(matching)))
            this.replaceMaterial(mesh, material, type, rule.limit)
        }
      })
    })
  }

  private handleSizeMatching(rule: RuleItem, type: MaterialSwitchType) {}

  private matchingStrategy = {
    matchSize: (rule: RuleItem, type: MaterialSwitchType) => this.handleSizeMatching(rule, type),
    matchName: (rule: RuleItem, type: MaterialSwitchType) => this.handleNameMatching(rule, type),
  }

  private handleMatch(type: MaterialSwitchType) {
    this.handleGroundMatching(type)

    if (type === SwitchType.Real)
      return

    this.objectsMap[type].forEach((object) => {
      object.traverse((child) => {
        if (child.type === 'Mesh') {
          if (child.userData.isMatched)
            this.resetMaterial(child as Mesh, type)
        }

        child.userData.isMatched = false
      })
    })

    if (this.rules.length > 0) {
      this.rules.forEach((rule) => {
        this.matchingStrategy[rule.type](rule, type)
      })
    }
  }

  // fixme: 如果是 model 会触发一次多余的删除，因为 gltf 加载的时候已经添加在场景中了，
  // 此处，再一次添加进入了场景，thrrejs 在 add 之前会删除原来的模型
  private handleNormalObject(object: Object3D, uuid: string): void {
    const realObject = object
    this.generateRealObject(realObject)
    this.objectsMap.Real.set(uuid, realObject)
    this.add(realObject)

    const fresnelObject = object.clone()
    this.generateFresnelObject(fresnelObject)
    this.objectsMap.Fresnel.set(uuid, fresnelObject)
    this.add(fresnelObject)

    const pureObject = object.clone()
    this.generatePureObject(pureObject)
    this.objectsMap.Pure.set(uuid, pureObject)
    this.add(pureObject)

    const frameObject = object.clone()
    this.generateFrameObject(frameObject)
    this.objectsMap.Frame.set(uuid, frameObject)
    this.add(frameObject)

    this.uniforms.maxSize.value = this.getMaxSize()
  }

  public removeObject(object: Object3D): void {
    return
    // 暂时注释：材质切换逻辑（2026.03.09）
    const uuid = this.getObjectUuid(object)
    this.objects.delete(uuid)

    Object.values(this.objectsMap).forEach((objectMap) => {
      const obj = objectMap.get(uuid)
      if (obj) {
        obj.removeFromParent()
        objectMap.delete(uuid)
      }
    })

    this.uniforms.maxSize.value = this.getMaxSize()
  }

  private createGroundMesh(): Mesh {
    const box = this.box3
    const size = box.getSize(new Vector3())
    const center = box.getCenter(new Vector3())

    const plane = new PlaneGeometry(size.x, size.z)
    const material = new MeshBasicMaterial({ color: new Color('#fff') })
    const mesh = new Mesh(plane, material)

    mesh.rotation.x = -Math.PI / 2
    mesh.position.set(center.x, GROUND_Y_OFFSET, center.z)

    return mesh
  }

  private updateGroundMesh(type: MaterialSwitchType): void {
    if (!this.objectsMap[type].has('ground'))
      return

    const box = this.box3
    const size = box.getSize(new Vector3())
    const center = box.getCenter(new Vector3());

    (this.objectsMap[type].get('ground') as Mesh).geometry.dispose();
    (this.objectsMap[type].get('ground') as Mesh).geometry = new PlaneGeometry(size.x, size.z);
    (this.objectsMap[type].get('ground') as Mesh).position.set(center.x, GROUND_Y_OFFSET, center.z)
  }

  private get box3(): Box3 {
    const box3 = new Box3()
    this.objectsMap.Real.forEach((object) => {
      box3.expandByObject(object)
    })
    return box3
  }

  private getMaxSize(): number {
    const box3 = this.box3
    const vertices = this.getBoxVertices(box3)
    const maxDistance = this.calculateMaxDistance(vertices)
    return maxDistance * SIZE_SCALE_FACTOR
  }

  private getBoxVertices(box3: Box3): Vector3[] {
    return [
      new Vector3(box3.min.x, box3.min.y, box3.min.z),
      new Vector3(box3.min.x, box3.min.y, box3.max.z),
      new Vector3(box3.min.x, box3.max.y, box3.min.z),
      new Vector3(box3.min.x, box3.max.y, box3.max.z),
      new Vector3(box3.max.x, box3.min.y, box3.min.z),
      new Vector3(box3.max.x, box3.min.y, box3.max.z),
      new Vector3(box3.max.x, box3.max.y, box3.min.z),
      new Vector3(box3.max.x, box3.max.y, box3.max.z),
    ]
  }

  private calculateMaxDistance(vertices: Vector3[]): number {
    return vertices.reduce((max, vertex) => {
      const distance = this.center.distanceTo(vertex)
      return Math.max(max, distance)
    }, 0)
  }

  private generateFresnelObject(object: Object3D): void {
    injectToFinirMaterial(object, this.uniforms)
  }

  private generateRealObject(object: Object3D): void {
    injectToRealFade(object, this.uniforms)
  }

  private generatePureObject(object: Object3D): void {
    injectToBaseWrite(object, this.uniforms)
  }

  private generateFrameObject(object: Object3D): void {
    injectToFrameFade(object, this.uniforms)
  }

  private updateVisibility(): void {
    this.hideAllObjects()
    this.showObjectsByType(SwitchType.Real)
  }
}
